import { Component } from '@angular/core';
import { UserRestService } from '@app/services/rest/user-rest.service';
import { select, Store } from '@ngrx/store';
import { MenuService } from '@app/services/menu';
import { combineLatest, Observable } from 'rxjs';
import { JSONSchema7 } from 'json-schema';
import { Resolver } from '@stoplight/json-ref-resolver/resolver';
import userSchema from '@app/pages/user/userSchema_edit.json';
import * as Reducers from '@app/store/reducers';
import { map, switchMap } from 'rxjs/operators';
import { SessionItemData } from '@workflow/services/session-item-data';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslateService } from '@ngx-translate/core';

interface ViewModel {
	schema: JSONSchema7,
	user: SessionItemData
}

async function resolveShema(): Promise<JSONSchema7> {
	const result = await new Resolver().resolve(userSchema);
	return result.result;
}

@Component({
	selector: 'app-profile',
	templateUrl: './profile.component.html',
	styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent {
	public readonly viewModel$: Observable<ViewModel>;
	
	public readonly userFormId = 'form-edit-user';
	
	public readonly userEditSplitButton: SplitButtonElement = {
		title: 'actions.save',
		icon: 'save',
		type: 'primary',
		buttonType: 'submit',
		attrForm: this.userFormId,
	};
	public readonly userCancelSplitButton: SplitButtonElement = {
		title: 'actions.cancel',
		icon: 'x',
		type: 'default',
		callback: () => this.navigateAway(),
	};
	
	constructor(
		private readonly translateService: TranslateService,
		private readonly notification: NzNotificationService,
		private readonly userRestService: UserRestService,
		private readonly router: Router,
		store: Store<never>,
		menuService: MenuService,
	) {
		menuService.title = 'profile.title';
		
		const schema$ = resolveShema();
		
		const activeUser$ = store.pipe(
			select(Reducers.getUser),
			switchMap(({ username }) => userRestService.userByUsername$(username)),
			map(user => new SessionItemData({ userData: user })),
		);
		
		this.viewModel$ = combineLatest([
			schema$,
			activeUser$,
		]).pipe(map(([schema, user]) => ({
			schema,
			user,
		})));
	}
	
	private navigateAway(): void {
		this.router
			.navigate(['dashboard'])
			.catch(console.error);
	}
	
	public async saveUser(isFormValid: boolean, data: SessionItemData): Promise<void> {
		if (!isFormValid) {
			return;
		}
		
		const { username, old_password, password, password_confirm } = data.data.userData as Record<string, string | undefined>;
		
		if (password) {
			if (password !== password_confirm) {
				this.notifyError('password');
				return;
			} else {
				try {
					await this.userRestService.updatePassword({
						old_password,
						password,
						password_confirm,
					}, username);
				} catch (e) {
					this.notifyError('password.invalid');
					return;
				}
				
				this.notifySuccess('password');
			}
		}
		
		try {
			const {
				username,
				name,
				email
			} = data.data.userData;
			
			const user = await this.userRestService.updateUser({
				username,
				name,
				email,
			});
			
			this.notifySuccess('save', { user });
			
			this.navigateAway();
		} catch (err) {
			this.notifyError('unknown');
		}
	}
	
	private notifyError<Action extends string>(action: Action) {
		const texts = this.translateService.instant([`profile.${action}.error.title`, `profile.${action}.error.description`]);
		
		this.notification.error(texts[`profile.${action}.error.title`], texts[`profile.${action}.error.description`], {
			nzDuration: 2500,
		});
	}
	
	// eslint-disable-next-line @typescript-eslint/ban-types
	private notifySuccess<Action extends string>(action: Action, ctx?: Object) {
		const texts = this.translateService.instant([`profile.${action}.success.title`, `profile.${action}.success.description`], ctx);
		
		this.notification.success(texts[`profile.${action}.success.title`], texts[`profile.${action}.success.description`], {
			nzDuration: 2500,
		});
	}
}
